/* Snack Bar — Sıramatik Sistemi. POS ana ekranı, KDS (Mutfak/Bar), Sıramatik pano, Menü yönetimi, Raporlar. */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { UtensilsCrossed, Wine, Bell, Monitor, Printer, ClipboardList, BarChart3, Plus, Loader2, Check, Trash2, X, Volume2, ShoppingCart, LayoutGrid, Send, User as UserIcon, Download, TrendingUp, Users as UsersIcon, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const KIND_LBL = { yiyecek: 'Yiyecek', icecek: 'İçecek' };

// ===== POS Ana Ekran =====
export function SnackBarHome() {
    const [summary, setSummary] = useState(null);
    const load = useCallback(async () => {
        try { const r = await api.get('/snack-bar/reports/summary', { params: { period: 'today' } }); setSummary(r.data); } catch { /* */ }
    }, []);
    useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [load]);
    const t = summary?.totals || {};
    const cards = [
        { to: '/snack-bar/kitchen', icon: UtensilsCrossed, label: 'Mutfak KDS', color: 'bg-amber-600' },
        { to: '/snack-bar/bar-pos', icon: Wine, label: 'Bar KDS', color: 'bg-purple-600' },
        { to: '/snack-bar/display', icon: Monitor, label: 'Sıramatik Pano', color: 'bg-emerald-600' },
        { to: '/snack-bar/menu-admin', icon: ClipboardList, label: 'Menü Yönetimi', color: 'bg-slate-700' },
        { to: '/snack-bar/reports', icon: BarChart3, label: 'Raporlar', color: 'bg-indigo-600' },
    ];
    return (
        <div className="space-y-4" data-testid="snack-home">
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><UtensilsCrossed className="w-6 h-6 text-emerald-600" /> Snack Bar — POS</h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[['Toplam Sipariş', t.orders], ['Yiyecek Adet', t.food], ['İçecek Adet', t.drink], ['Ciro (TL)', t.revenue]].map(([lbl, v]) => (
                    <div key={lbl} className="bg-white rounded-lg p-3 border shadow-sm" data-testid={`snack-kpi-${lbl}`}>
                        <div className="text-xs text-slate-500">{lbl}</div>
                        <div className="text-2xl font-bold text-slate-800 mt-1">{v ?? '—'}</div>
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {cards.map(c => (
                    <Link key={c.to} to={c.to} data-testid={`snack-shortcut-${c.to.split('/').pop()}`} className={`${c.color} text-white rounded-xl p-6 shadow hover:opacity-90 transition text-center`}>
                        <c.icon className="w-8 h-8 mx-auto mb-2" />
                        <div className="font-semibold">{c.label}</div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

// ===== KDS (Kitchen & Bar) =====
export function SnackBarKDS({ station }) {
    const [orders, setOrders] = useState([]);
    const load = useCallback(async () => {
        try { const r = await api.get('/snack-bar/orders', { params: { station, active_only: true } }); setOrders(r.data.items || []); } catch { /* */ }
    }, [station]);
    useEffect(() => { load(); const i = setInterval(load, 3000); return () => clearInterval(i); }, [load]);
    const markReady = async (oid, lineId) => {
        try { await api.post(`/snack-bar/orders/${oid}/items/${lineId}/ready`); load(); }
        catch { toast.error('Hata'); }
    };
    const title = station === 'kitchen' ? 'Mutfak KDS' : 'Bar KDS';
    const Icon = station === 'kitchen' ? UtensilsCrossed : Wine;
    return (
        <div className="space-y-3" data-testid={`kds-${station}`}>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Icon className="w-6 h-6 text-emerald-600" /> {title}</h1>
            {orders.length === 0 ? <div className="text-center py-10 bg-white rounded border text-slate-400" data-testid="kds-empty">Bekleyen sipariş yok</div> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {orders.map(o => (
                        <Card key={o.id} className="border-l-4 border-amber-500" data-testid={`kds-order-${o.order_no}`}>
                            <CardHeader className="pb-2">
                                <div className="flex justify-between items-center">
                                    <CardTitle className="text-lg font-mono text-amber-700">#{o.order_no}</CardTitle>
                                    <Badge className="bg-slate-800 text-white">{o.table_name}</Badge>
                                </div>
                                <div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleTimeString('tr-TR')}</div>
                            </CardHeader>
                            <CardContent className="space-y-1">
                                {o.items.map(it => (
                                    <div key={it.line_id} className={`flex justify-between items-center p-2 rounded text-sm ${it.status === 'ready' ? 'bg-emerald-50 text-slate-500 line-through' : 'bg-slate-50'}`}>
                                        <div><b>{it.qty}x</b> {it.name}{it.note && <div className="text-xs text-orange-700">Not: {it.note}</div>}</div>
                                        {it.status !== 'ready' && <Button size="sm" onClick={() => markReady(o.id, it.line_id)} className="bg-emerald-600 hover:bg-emerald-700 h-7" data-testid={`kds-ready-${it.line_id}`}><Check className="w-3 h-3 mr-1" />Hazır</Button>}
                                    </div>
                                ))}
                                {o.guest_note && <div className="text-xs bg-yellow-50 text-yellow-800 p-1 rounded">Not: {o.guest_note}</div>}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

// ===== Sıramatik Pano (TV) =====
export function SnackBarDisplay() {
    const [items, setItems] = useState([]);
    const [seen, setSeen] = useState(new Set());
    const load = useCallback(async () => {
        try {
            const r = await api.get('/snack-bar/display');
            const list = r.data.items || [];
            const newOnes = list.filter(x => !seen.has(x.order_no));
            if (newOnes.length > 0) {
                try {
                    const ctx = new (window.AudioContext || window.webkitAudioContext)();
                    const osc = ctx.createOscillator(); const gain = ctx.createGain();
                    osc.connect(gain); gain.connect(ctx.destination);
                    osc.frequency.value = 880; gain.gain.value = 0.3;
                    osc.start(); setTimeout(() => { osc.frequency.value = 1200; }, 150); setTimeout(() => { osc.stop(); ctx.close(); }, 400);
                } catch { /* no audio */ }
            }
            setSeen(new Set(list.map(x => x.order_no)));
            setItems(list);
        } catch { /* */ }
    }, [seen]);
    useEffect(() => { load(); const i = setInterval(load, 3000); return () => clearInterval(i); }, [load]);
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 -m-4 md:-m-6 p-8" data-testid="snack-display">
            <div className="text-center mb-6">
                <h1 className="text-5xl font-black text-emerald-400 mb-2">SIPARIŞINIZ HAZIR</h1>
                <p className="text-slate-400 text-lg">Numaranızı bekleyiniz</p>
            </div>
            {items.length === 0 ? (
                <div className="text-center text-slate-500 text-xl mt-20">Şu an hazır sipariş yok</div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="display-grid">
                    {items.map(o => (
                        <div key={o.order_no} className="bg-emerald-500 text-white rounded-3xl p-8 text-center shadow-2xl animate-pulse" data-testid={`display-${o.order_no}`}>
                            <div className="text-sm opacity-80">Sipariş No</div>
                            <div className="text-6xl font-black my-2">{o.order_no}</div>
                            <div className="text-2xl">{o.table_name}</div>
                        </div>
                    ))}
                </div>
            )}
            <div className="fixed bottom-4 right-4 text-slate-500 text-xs flex items-center gap-1"><Volume2 className="w-3 h-3" />Sesli bildirim aktif</div>
        </div>
    );
}

// ===== Menü / Masa / Yazıcı Yönetimi =====
export function SnackBarAdmin() {
    const [tab, setTab] = useState('menu');
    const ADMIN_TABS = [
        { id: 'menu', label: 'Menü', icon: ClipboardList },
        { id: 'tables', label: 'Masalar', icon: LayoutGrid },
        { id: 'printers', label: 'Yazıcılar', icon: Printer },
        { id: 'qr', label: 'QR Kod', icon: Sparkles },
        { id: 'tv', label: 'TV Tanıtım', icon: Monitor },
    ];
    return (
        <div className="space-y-3" data-testid="snack-admin">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center shadow">
                    <ClipboardList className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                    <h2 className="text-base font-bold text-slate-800">Snack Bar Yönetimi</h2>
                    <p className="text-[11px] text-slate-500">Menü · Masa · Yazıcı · QR · TV Tanıtım</p>
                </div>
            </div>
            <div className="bg-white rounded-lg border p-1 inline-flex flex-wrap gap-1">
                {ADMIN_TABS.map(x => {
                    const active = tab === x.id;
                    return (
                        <button
                            key={x.id}
                            onClick={() => setTab(x.id)}
                            data-testid={`admin-tab-${x.id}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${active ? 'bg-slate-800 text-white shadow' : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                            <x.icon className="w-3.5 h-3.5" />{x.label}
                        </button>
                    );
                })}
            </div>
            {tab === 'menu' && <MenuAdmin />}
            {tab === 'tables' && <TablesAdmin />}
            {tab === 'printers' && <PrintersAdmin />}
            {tab === 'qr' && <QrAdmin />}
            {tab === 'tv' && <TvMediaAdmin />}
        </div>
    );
}

function MenuAdmin() {
    const [cats, setCats] = useState([]); const [items, setItems] = useState([]);
    const [catForm, setCatForm] = useState({ name: '', kind: 'yiyecek', sort_order: 0 });
    const [itemForm, setItemForm] = useState({ category_id: '', name: '', price: 0, photo_url: '', kind: 'yiyecek', description: '' });
    const load = async () => {
        const [c, i] = await Promise.all([api.get('/snack-bar/categories'), api.get('/snack-bar/menu-items')]);
        setCats(c.data.items || []); setItems(i.data.items || []);
    };
    useEffect(() => { load(); }, []);
    const addCat = async () => { if (!catForm.name) return; await api.post('/snack-bar/categories', catForm); setCatForm({ name: '', kind: 'yiyecek', sort_order: 0 }); load(); };
    const delCat = async (id) => { await api.delete(`/snack-bar/categories/${id}`); load(); };
    const addItem = async () => { if (!itemForm.name || !itemForm.category_id) return toast.error('Kategori ve ad zorunlu'); await api.post('/snack-bar/menu-items', { ...itemForm, price: Number(itemForm.price) }); setItemForm({ category_id: '', name: '', price: 0, photo_url: '', kind: itemForm.kind, description: '' }); load(); };
    const delItem = async (id) => { await api.delete(`/snack-bar/menu-items/${id}`); load(); };
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card><CardHeader className="pb-2"><CardTitle className="text-base">Kategoriler</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    <div className="flex gap-1">
                        <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} placeholder="Kategori adı" data-testid="cat-name" />
                        <Select value={catForm.kind} onValueChange={(v) => setCatForm({ ...catForm, kind: v })}>
                            <SelectTrigger className="w-32" data-testid="cat-kind"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="yiyecek">Yiyecek</SelectItem><SelectItem value="icecek">İçecek</SelectItem></SelectContent>
                        </Select>
                        <Button onClick={addCat} data-testid="cat-add"><Plus className="w-3 h-3" /></Button>
                    </div>
                    <div className="space-y-1">{cats.map(c => (
                        <div key={c.id} className="flex justify-between items-center bg-slate-50 p-2 rounded text-sm">
                            <span><b>{c.name}</b> <Badge className="bg-slate-200 text-slate-700 ml-1">{KIND_LBL[c.kind]}</Badge></span>
                            <Button size="sm" variant="ghost" onClick={() => delCat(c.id)}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                        </div>
                    ))}</div>
                </CardContent></Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-base">Ürünler ({items.length})</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    <div className="grid grid-cols-2 gap-1">
                        <Select value={itemForm.category_id} onValueChange={(v) => { const c = cats.find(x => x.id === v); setItemForm({ ...itemForm, category_id: v, kind: c?.kind || itemForm.kind }); }}>
                            <SelectTrigger data-testid="item-cat"><SelectValue placeholder="Kategori" /></SelectTrigger>
                            <SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} placeholder="Ürün adı" data-testid="item-name" />
                        <Input type="number" step="0.01" value={itemForm.price} onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })} placeholder="Fiyat (TL)" data-testid="item-price" />
                        <Input value={itemForm.photo_url} onChange={(e) => setItemForm({ ...itemForm, photo_url: e.target.value })} placeholder="Foto URL (ops.)" data-testid="item-photo" />
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                        <Label className="text-xs text-slate-600 shrink-0">📸 Görsel Yükle:</Label>
                        <Input type="file" accept="image/*" onChange={async (e) => {
                            const file = e.target.files?.[0]; if (!file) return;
                            try {
                                const fd = new FormData(); fd.append('file', file);
                                const r = await api.post('/snack-bar/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                setItemForm(f => ({ ...f, photo_url: r.data.url }));
                                toast.success('Görsel yüklendi');
                            } catch (err) { toast.error(err?.response?.data?.detail || 'Yükleme başarısız'); }
                        }} data-testid="item-file-upload" className="text-xs h-8" />
                        {itemForm.photo_url && <img src={itemForm.photo_url.startsWith('http') ? itemForm.photo_url : `${process.env.REACT_APP_BACKEND_URL}${itemForm.photo_url}`} alt="" className="w-10 h-10 object-cover rounded border" />}
                    </div>
                    <Button onClick={addItem} className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="item-add"><Plus className="w-3 h-3 mr-1" />Ürün Ekle</Button>
                    <div className="max-h-64 overflow-y-auto space-y-1">{items.map(it => (
                        <div key={it.id} className="flex justify-between items-center bg-slate-50 p-2 rounded text-sm gap-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                {it.photo_url ? <img src={it.photo_url.startsWith('http') ? it.photo_url : `${process.env.REACT_APP_BACKEND_URL}${it.photo_url}`} alt="" className="w-10 h-10 object-cover rounded shrink-0" /> :
                                    <div className="w-10 h-10 rounded bg-slate-200 flex items-center justify-center shrink-0 text-slate-400 text-xs">—</div>}
                                <span className="truncate"><b>{it.name}</b> · {it.price} TL · <Badge className="bg-slate-200 text-slate-700">{KIND_LBL[it.kind]}</Badge></span>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => delItem(it.id)}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                        </div>
                    ))}</div>
                </CardContent></Card>
        </div>
    );
}

function TablesAdmin() {
    const [tables, setTables] = useState([]);
    const [form, setForm] = useState({ name: '', zone: '', capacity: 4 });
    const load = async () => { const r = await api.get('/snack-bar/tables'); setTables(r.data.items || []); };
    useEffect(() => { load(); }, []);
    const add = async () => { if (!form.name) return; await api.post('/snack-bar/tables', { ...form, capacity: Number(form.capacity) }); setForm({ name: '', zone: '', capacity: 4 }); load(); };
    const del = async (id) => { await api.delete(`/snack-bar/tables/${id}`); load(); };
    return (
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Masalar ({tables.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
                <div className="flex gap-1">
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Masa adı (M1)" data-testid="table-name" />
                    <Input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="Bölge" data-testid="table-zone" />
                    <Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="w-20" data-testid="table-cap" />
                    <Button onClick={add} data-testid="table-add"><Plus className="w-3 h-3" /></Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">{tables.map(t => (
                    <div key={t.id} className="bg-slate-50 p-2 rounded text-sm flex justify-between">
                        <span><b>{t.name}</b> {t.zone && `· ${t.zone}`} · {t.capacity}k</span>
                        <Button size="sm" variant="ghost" onClick={() => del(t.id)}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                    </div>
                ))}</div>
            </CardContent></Card>
    );
}

function PrintersAdmin() {
    const [printers, setPrinters] = useState([]);
    const [k, setK] = useState({ station: 'kitchen', ip: '', port: 9100 });
    const [b, setB] = useState({ station: 'bar', ip: '', port: 9100 });
    const load = async () => {
        const r = await api.get('/snack-bar/printers'); const items = r.data.items || [];
        setPrinters(items);
        const kk = items.find(x => x.station === 'kitchen'); if (kk) setK({ station: 'kitchen', ip: kk.ip, port: kk.port || 9100 });
        const bb = items.find(x => x.station === 'bar'); if (bb) setB({ station: 'bar', ip: bb.ip, port: bb.port || 9100 });
    };
    useEffect(() => { load(); }, []);
    const save = async (payload) => { await api.post('/snack-bar/printers', payload); toast.success('Kaydedildi'); load(); };
    return (
        <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Printer className="w-4 h-4" />Yazıcı Ayarları (ESC/POS Network)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
                {[['Mutfak', k, setK], ['Bar', b, setB]].map(([lbl, st, setSt]) => (
                    <div key={lbl} className="bg-slate-50 p-3 rounded space-y-2">
                        <div className="font-semibold text-sm">{lbl} Yazıcısı ({st.station})</div>
                        <div className="grid grid-cols-3 gap-1">
                            <Input value={st.ip} onChange={(e) => setSt({ ...st, ip: e.target.value })} placeholder="IP (192.168.1.100)" data-testid={`printer-ip-${st.station}`} />
                            <Input type="number" value={st.port} onChange={(e) => setSt({ ...st, port: Number(e.target.value) })} placeholder="Port" data-testid={`printer-port-${st.station}`} />
                            <div className="flex gap-1">
                                <Button onClick={() => save({ station: st.station, ip: st.ip, port: Number(st.port), active: true })} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid={`printer-save-${st.station}`}>Kaydet</Button>
                                <Button variant="outline" onClick={async () => {
                                    const p = printers.find(x => x.station === st.station);
                                    if (!p) return toast.error('Önce kaydet');
                                    toast.info('Test fişi gönderiliyor…');
                                    try {
                                        const r = await api.post(`/snack-bar/printers/${p.id}/test`);
                                        if (r.data.ok) toast.success(`✓ Test başarılı (deneme ${r.data.attempt})`);
                                        else toast.error(`✗ ${r.data.error}`);
                                    } catch (e) { toast.error(e?.response?.data?.detail || 'Hata'); }
                                }} data-testid={`printer-test-${st.station}`}><Printer className="w-3 h-3 mr-1" />Test</Button>
                            </div>
                        </div>
                    </div>
                ))}
                <div className="text-xs text-slate-500 bg-emerald-50 p-2 rounded">
                    💡 <b>Xprinter</b> ağa bağlı ESC/POS yazıcılar desteklenir. IP + Port (9100) girin, önce <b>Kaydet</b> sonra <b>Test</b> butonu ile bağlantıyı doğrulayın. Test 2 kez dener.
                </div>
            </CardContent></Card>
    );
}

// ===== Raporlar (Advanced Analytics) =====
export function SnackBarReports() {
    const today = new Date().toISOString().slice(0, 10);
    const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
    const [dateFrom, setDateFrom] = useState(daysAgo(6));
    const [dateTo, setDateTo] = useState(today);
    const [preset, setPreset] = useState('7d');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const applyPreset = (p) => {
        setPreset(p);
        if (p === 'today') { setDateFrom(today); setDateTo(today); }
        else if (p === '7d') { setDateFrom(daysAgo(6)); setDateTo(today); }
        else if (p === '30d') { setDateFrom(daysAgo(29)); setDateTo(today); }
        else if (p === 'mtd') { const d = new Date(); d.setDate(1); setDateFrom(d.toISOString().slice(0, 10)); setDateTo(today); }
    };

    useEffect(() => {
        setLoading(true);
        api.get('/snack-bar/analytics/summary', { params: { date_from: dateFrom, date_to: dateTo } })
            .then(r => setData(r.data))
            .catch(() => toast.error('Analitik yüklenemedi'))
            .finally(() => setLoading(false));
    }, [dateFrom, dateTo]);

    const downloadPdf = async () => {
        try {
            const r = await api.get('/snack-bar/analytics/pdf', { params: { date_from: dateFrom, date_to: dateTo }, responseType: 'blob' });
            const blob = new Blob([r.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `snack-analitik-${dateFrom}_${dateTo}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error('PDF indirilemedi'); }
    };

    const t = data?.totals || {};
    const ws = data?.wait_stats;
    const trend = data?.trend || [];
    const topItems = data?.top_items || [];
    const hourly = data?.hourly || [];
    const waiters = data?.waiters || [];
    const tables = data?.tables || [];

    const fmt = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
        <div className="space-y-4" data-testid="snack-reports">
            {/* Header */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-5 text-white shadow-xl relative overflow-hidden">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #10b981 0%, transparent 40%), radial-gradient(circle at 80% 70%, #eab308 0%, transparent 40%)' }} />
                <div className="relative flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold tracking-tight">Snack Bar Analitik</h2>
                            <p className="text-xs text-slate-400">Ciro, ürün, garson, saat, masa ve bekleme metrikleri</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex bg-slate-800/60 rounded-lg p-0.5">
                            {[['today', 'Bugün'], ['7d', 'Son 7g'], ['30d', 'Son 30g'], ['mtd', 'Ay Başı']].map(([k, l]) => (
                                <button key={k} onClick={() => applyPreset(k)} data-testid={`an-preset-${k}`}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${preset === k ? 'bg-emerald-500 text-white shadow' : 'text-slate-300 hover:text-white'}`}>{l}</button>
                            ))}
                        </div>
                        <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreset(''); }} data-testid="an-date-from"
                            className="h-8 bg-slate-800/60 border-slate-700 text-white text-xs w-36" />
                        <span className="text-slate-500">-</span>
                        <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPreset(''); }} data-testid="an-date-to"
                            className="h-8 bg-slate-800/60 border-slate-700 text-white text-xs w-36" />
                        <Button onClick={downloadPdf} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" data-testid="an-pdf">
                            <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                    </div>
                </div>
            </div>

            {loading && <div className="text-center text-slate-500 py-2 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Yükleniyor…</div>}

            {/* KPI Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
                {[
                    { lbl: 'Sipariş', v: t.orders || 0, col: 'from-slate-600 to-slate-700', icon: ClipboardList },
                    { lbl: 'Adet', v: t.items || 0, col: 'from-indigo-600 to-indigo-700', icon: LayoutGrid },
                    { lbl: 'Yiyecek', v: t.food || 0, col: 'from-amber-500 to-orange-600', icon: UtensilsCrossed },
                    { lbl: 'İçecek', v: t.drink || 0, col: 'from-purple-500 to-purple-700', icon: Wine },
                    { lbl: 'Ciro (TL)', v: fmt(t.revenue), col: 'from-emerald-500 to-teal-600', icon: TrendingUp, big: true },
                    { lbl: 'Yiyecek TL', v: fmt(t.food_revenue), col: 'from-amber-600 to-amber-700', icon: null },
                    { lbl: 'İçecek TL', v: fmt(t.drink_revenue), col: 'from-purple-600 to-purple-800', icon: null },
                    { lbl: 'Ort. Sepet', v: fmt(t.avg_basket), col: 'from-teal-600 to-emerald-700', icon: null },
                ].map((k) => (
                    <div key={k.lbl} className={`bg-gradient-to-br ${k.col} text-white rounded-xl p-3 shadow-md`} data-testid={`an-kpi-${k.lbl}`}>
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/80">
                            {k.icon && <k.icon className="w-3 h-3" />}{k.lbl}
                        </div>
                        <div className={`font-bold mt-1 ${k.big ? 'text-xl' : 'text-lg'} truncate`}>{k.v}</div>
                    </div>
                ))}
            </div>

            {/* Wait time */}
            {ws && (
                <Card className="border-l-4 border-emerald-500">
                    <CardContent className="p-3 flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
                            <Clock className="w-4 h-4 text-emerald-600" /> Hazırlama Süresi
                        </div>
                        <div className="flex gap-6 text-xs">
                            <div><span className="text-slate-500">Ölçüm:</span> <b>{ws.count}</b></div>
                            <div><span className="text-slate-500">Ortalama:</span> <b className="text-emerald-700">{ws.avg_min} dk</b></div>
                            <div><span className="text-slate-500">Medyan:</span> <b>{ws.p50_min} dk</b></div>
                            <div><span className="text-slate-500">p90:</span> <b className="text-orange-700">{ws.p90_min} dk</b></div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Trend + Hourly */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <Card className="lg:col-span-2">
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600" />Günlük Ciro Trendi</CardTitle></CardHeader>
                    <CardContent>
                        {trend.length === 0 ? <div className="text-center text-slate-400 py-8 text-sm">Kayıt yok</div> : (
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart data={trend}>
                                    <defs>
                                        <linearGradient id="g-rev" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="date" fontSize={10} /><YAxis fontSize={10} />
                                    <Tooltip formatter={(v, n) => n === 'revenue' ? `${fmt(v)} TL` : v} />
                                    <Area type="monotone" dataKey="revenue" stroke="#059669" fill="url(#g-rev)" strokeWidth={2} name="Ciro (TL)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-purple-600" />Saatlik Yoğunluk</CardTitle></CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={hourly}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="hour" fontSize={9} tickFormatter={(h) => `${h}:00`} />
                                <YAxis fontSize={10} />
                                <Tooltip labelFormatter={(h) => `Saat ${h}:00`} />
                                <Bar dataKey="orders" fill="#8b5cf6" name="Sipariş" radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            {/* Yiyecek/İçecek trend + Top items */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Yiyecek & İçecek — Günlük Adet</CardTitle></CardHeader>
                    <CardContent>
                        {trend.length === 0 ? <div className="text-center text-slate-400 py-8 text-sm">Kayıt yok</div> : (
                            <ResponsiveContainer width="100%" height={240}>
                                <LineChart data={trend}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="date" fontSize={9} /><YAxis fontSize={10} />
                                    <Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} />
                                    <Line type="monotone" dataKey="food" stroke="#d97706" strokeWidth={2} name="Yiyecek" dot={false} />
                                    <Line type="monotone" dataKey="drink" stroke="#9333ea" strokeWidth={2} name="İçecek" dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">🏆 En Çok Satan Ürünler (Top 10)</CardTitle></CardHeader>
                    <CardContent>
                        {topItems.length === 0 ? <div className="text-center text-slate-400 py-8 text-sm">Kayıt yok</div> : (
                            <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
                                {topItems.map((it, i) => {
                                    const max = topItems[0].qty || 1;
                                    const pct = (it.qty / max) * 100;
                                    return (
                                        <div key={i} className="relative bg-slate-50 rounded p-2 overflow-hidden" data-testid={`an-top-${i}`}>
                                            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-100 to-emerald-50" style={{ width: `${pct}%` }} />
                                            <div className="relative flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${i < 3 ? 'bg-amber-400 text-white' : 'bg-slate-300 text-slate-700'}`}>{i + 1}</span>
                                                    <span className="text-sm font-medium text-slate-800 truncate">{it.name}</span>
                                                    <Badge className={`text-[9px] ${it.kind === 'yiyecek' ? 'bg-amber-100 text-amber-800' : 'bg-purple-100 text-purple-800'}`}>{KIND_LBL[it.kind]}</Badge>
                                                </div>
                                                <div className="text-xs shrink-0">
                                                    <b className="text-slate-800">{it.qty}</b> adet · <b className="text-emerald-700">{fmt(it.revenue)}</b> TL
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Waiters + Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><UsersIcon className="w-4 h-4 text-indigo-600" />Garson Performansı</CardTitle></CardHeader>
                    <CardContent>
                        {waiters.length === 0 ? <div className="text-center text-slate-400 py-8 text-sm">Kayıt yok</div> : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead className="text-slate-500 border-b">
                                        <tr><th className="text-left py-1.5">Garson</th><th>Sipariş</th><th>Adet</th><th className="text-right">Ciro (TL)</th><th>Ort. Bek. (dk)</th></tr>
                                    </thead>
                                    <tbody>
                                        {waiters.map((w, i) => (
                                            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`an-waiter-${i}`}>
                                                <td className="py-1.5 font-medium text-slate-800">{w.name}</td>
                                                <td className="text-center">{w.orders}</td>
                                                <td className="text-center">{w.items}</td>
                                                <td className="text-right font-bold text-emerald-700">{fmt(w.revenue)}</td>
                                                <td className="text-center">{w.avg_wait_min ?? '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">🍽 Masa Bazlı Ciro (Top 10)</CardTitle></CardHeader>
                    <CardContent>
                        {tables.length === 0 ? <div className="text-center text-slate-400 py-8 text-sm">Kayıt yok</div> : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {tables.map((tb, i) => (
                                    <div key={i} className="bg-gradient-to-br from-slate-50 to-white border rounded-lg p-2 text-center" data-testid={`an-table-${i}`}>
                                        <div className="text-[10px] text-slate-500 uppercase">Masa</div>
                                        <div className="text-lg font-bold text-slate-800">{tb.name}</div>
                                        <div className="text-[10px] text-slate-500">{tb.orders} sipariş</div>
                                        <div className="text-sm font-bold text-emerald-700 mt-0.5">{fmt(tb.revenue)} TL</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

// ===== Misafir QR Sayfası =====
export function GuestOrder() {
    const [step, setStep] = useState('table'); // table | menu | success
    const [tables, setTables] = useState([]);
    const [table, setTable] = useState(null);
    const [menu, setMenu] = useState({ categories: [], items: [] });
    const [cart, setCart] = useState([]); // {menu_item_id, name, qty, price, kind}
    const [note, setNote] = useState('');
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get('/snack-bar/public/tables').then(r => setTables(r.data.items || []));
        api.get('/snack-bar/public/menu').then(r => setMenu(r.data));
    }, []);
    const add = (m) => setCart(c => {
        const ex = c.find(x => x.menu_item_id === m.id);
        if (ex) return c.map(x => x.menu_item_id === m.id ? { ...x, qty: x.qty + 1 } : x);
        return [...c, { menu_item_id: m.id, name: m.name, qty: 1, price: m.price, kind: m.kind }];
    });
    const dec = (id) => setCart(c => c.map(x => x.menu_item_id === id ? { ...x, qty: x.qty - 1 } : x).filter(x => x.qty > 0));
    const submit = async () => {
        if (!cart.length) return toast.error('Sepetiniz boş');
        setBusy(true);
        try {
            const r = await api.post('/snack-bar/public/orders', {
                table_id: table.id,
                items: cart.map(x => ({ menu_item_id: x.menu_item_id, qty: x.qty })),
                guest_note: note,
            });
            setResult(r.data); setStep('success');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Hata'); }
        finally { setBusy(false); }
    };
    const total = cart.reduce((s, x) => s + x.qty * x.price, 0);
    return (
        <div className="min-h-screen bg-slate-50 p-4" data-testid="guest-order">
            <div className="max-w-lg mx-auto space-y-3">
                <div className="text-center py-2">
                    <UtensilsCrossed className="w-10 h-10 mx-auto text-emerald-600" />
                    <h1 className="text-2xl font-bold">Grand Kaptan Snack Bar</h1>
                    {table && <div className="text-sm text-slate-500">Masa: <b>{table.name}</b></div>}
                </div>
                {step === 'table' && (
                    <Card><CardHeader><CardTitle>Masanızı Seçin</CardTitle></CardHeader>
                        <CardContent><div className="grid grid-cols-3 gap-2">
                            {tables.map(t => (
                                <Button key={t.id} onClick={() => { setTable(t); setStep('menu'); }} className="bg-white text-slate-800 hover:bg-emerald-50 border" data-testid={`gt-${t.name}`}><b>{t.name}</b></Button>
                            ))}
                        </div></CardContent></Card>
                )}
                {step === 'menu' && (
                    <>
                        {menu.categories.map(cat => {
                            const catItems = menu.items.filter(x => x.category_id === cat.id);
                            if (!catItems.length) return null;
                            return (
                                <Card key={cat.id}><CardHeader className="pb-1"><CardTitle className="text-base flex items-center gap-1">
                                    {cat.kind === 'yiyecek' ? <UtensilsCrossed className="w-4 h-4" /> : <Wine className="w-4 h-4" />}
                                    {cat.name}
                                </CardTitle></CardHeader>
                                    <CardContent className="space-y-1">
                                        {catItems.map(m => {
                                            const inCart = cart.find(x => x.menu_item_id === m.id);
                                            return (
                                                <div key={m.id} className="flex items-center justify-between p-2 bg-slate-50 rounded" data-testid={`gm-${m.id}`}>
                                                    <div className="flex-1">
                                                        <div className="font-medium">{m.name}</div>
                                                        <div className="text-xs text-slate-500">{m.price} TL</div>
                                                    </div>
                                                    {inCart ? (
                                                        <div className="flex items-center gap-1">
                                                            <Button size="sm" variant="outline" onClick={() => dec(m.id)} className="h-7 w-7 p-0" data-testid={`gm-dec-${m.id}`}>-</Button>
                                                            <span className="w-6 text-center font-bold">{inCart.qty}</span>
                                                            <Button size="sm" onClick={() => add(m)} className="bg-emerald-600 h-7 w-7 p-0" data-testid={`gm-inc-${m.id}`}>+</Button>
                                                        </div>
                                                    ) : (
                                                        <Button size="sm" onClick={() => add(m)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`gm-add-${m.id}`}><Plus className="w-3 h-3" /></Button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </CardContent></Card>
                            );
                        })}
                        <Card><CardContent className="p-3 space-y-2">
                            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not (opsiyonel — alerjen, özel istek)" data-testid="gm-note" />
                            <div className="flex justify-between font-bold">
                                <span>Sepet ({cart.reduce((s, x) => s + x.qty, 0)} adet)</span>
                                <span>{total.toFixed(2)} TL</span>
                            </div>
                            <Button onClick={submit} disabled={busy || !cart.length} className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6" data-testid="gm-submit">
                                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}Siparişi Ver
                            </Button>
                        </CardContent></Card>
                    </>
                )}
                {step === 'success' && result && (
                    <Card data-testid="gm-success">
                        <CardContent className="p-6 text-center space-y-3">
                            <Check className="w-16 h-16 text-emerald-600 mx-auto" />
                            <div className="text-xl font-bold">Siparişiniz Alındı</div>
                            <div className="text-sm text-slate-500">Sipariş Numaranız</div>
                            <div className="text-6xl font-black text-emerald-600" data-testid="gm-order-no">{result.order_no}</div>
                            <div className="text-sm text-slate-600">Numaranızı ekranda takip edin. Hazır olduğunda ilan edilecek.</div>
                            <Button onClick={() => { setCart([]); setNote(''); setResult(null); setStep('table'); }} variant="outline" className="w-full">Yeni Sipariş</Button>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}

// ===== Snack Bar POS — Unified Ana Ekran (Garson + Admin) =====
// Tek ekranda: Sipariş Al (masa + menü + sepet) · KDS · Sıramatik · (admin) Menü Yönetimi · (admin) Raporlar
export function SnackBarPOS() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'manager';
    const [tab, setTab] = useState('order');

    // ----- POS Sipariş Al state -----
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);
    const [menu, setMenu] = useState({ categories: [], items: [] });
    const [activeCat, setActiveCat] = useState(null);
    const [cart, setCart] = useState([]);
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [lastOrder, setLastOrder] = useState(null);

    const loadMasterData = useCallback(async () => {
        try {
            const [t, m] = await Promise.all([
                api.get('/snack-bar/tables'),
                api.get('/snack-bar/menu-items'),
            ]);
            setTables(t.data.items || []);
            const cats = await api.get('/snack-bar/categories');
            setMenu({ categories: cats.data.items || [], items: m.data.items || [] });
            if ((cats.data.items || []).length && !activeCat) setActiveCat(cats.data.items[0].id);
        } catch (e) { /* noop */ }
    }, [activeCat]);
    useEffect(() => { loadMasterData(); }, [loadMasterData]);

    const cartTotal = useMemo(() => cart.reduce((s, x) => s + x.qty * x.price, 0), [cart]);
    const cartCount = useMemo(() => cart.reduce((s, x) => s + x.qty, 0), [cart]);

    const addToCart = (m) => setCart(c => {
        const ex = c.find(x => x.menu_item_id === m.id);
        if (ex) return c.map(x => x.menu_item_id === m.id ? { ...x, qty: x.qty + 1 } : x);
        return [...c, { menu_item_id: m.id, name: m.name, qty: 1, price: Number(m.price) || 0, kind: m.kind }];
    });
    const decCart = (id) => setCart(c => c.map(x => x.menu_item_id === id ? { ...x, qty: x.qty - 1 } : x).filter(x => x.qty > 0));
    const removeCart = (id) => setCart(c => c.filter(x => x.menu_item_id !== id));
    const clearCart = () => { setCart([]); setNote(''); };

    const submitOrder = async () => {
        if (!selectedTable) { toast.error('Önce masa seçin'); setTab('order'); return; }
        if (!cart.length) { toast.error('Sepet boş'); return; }
        setSubmitting(true);
        try {
            const r = await api.post('/snack-bar/pos/orders', {
                table_id: selectedTable.id,
                items: cart.map(x => ({ menu_item_id: x.menu_item_id, qty: x.qty })),
                guest_note: note,
            });
            setLastOrder(r.data);
            toast.success(`Sipariş #${r.data.order_no} POS'a gönderildi`);
            clearCart();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Sipariş gönderilemedi');
        } finally { setSubmitting(false); }
    };

    // Tabs config — garson için 3, admin için 5
    const TABS = [
        { id: 'order', label: 'Sipariş Al', icon: ShoppingCart, showAll: true },
        { id: 'kds', label: 'POS / KDS', icon: LayoutGrid, showAll: true },
        { id: 'display', label: 'Sıramatik', icon: Monitor, showAll: true },
        { id: 'menu-admin', label: 'Menü Yönetimi', icon: ClipboardList, showAll: false },
        { id: 'reports', label: 'Raporlar', icon: BarChart3, showAll: false },
    ].filter(t => t.showAll || isAdmin);

    return (
        <div className="space-y-4" data-testid="snack-pos-root">
            {/* Premium Header — Luxury Coastal */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-4 md:p-5 text-white shadow-xl relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.12] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 15% 20%, #10b981 0%, transparent 45%), radial-gradient(circle at 85% 80%, #eab308 0%, transparent 45%)' }} />
                <div className="relative flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 via-teal-500 to-emerald-700 flex items-center justify-center shadow-lg ring-1 ring-emerald-400/40">
                            <UtensilsCrossed className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-lg md:text-xl font-bold tracking-tight">Grand Kaptan · Snack Bar</h1>
                                <span className="text-[10px] uppercase tracking-widest bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full border border-amber-500/30">POS</span>
                            </div>
                            <p className="text-[11px] text-slate-400 mt-0.5">Sipariş · Mutfak/Bar · Sıramatik · Yönetim · Analitik</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs bg-slate-800/70 border border-slate-700 rounded-lg px-3 py-1.5 backdrop-blur">
                        <UserIcon className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="font-medium text-slate-100">{user?.full_name || user?.email}</span>
                        <Badge className={`ml-1 ${user?.role === 'garson' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
                            {user?.role === 'garson' ? 'Garson' : (user?.role === 'admin' ? 'Admin' : user?.role)}
                        </Badge>
                        {selectedTable && (
                            <>
                                <span className="text-slate-500">|</span>
                                <span className="text-slate-300">Masa: <b className="text-white">{selectedTable.name}</b></span>
                                <Button size="sm" variant="ghost" className="h-5 px-1 text-slate-400 hover:text-white hover:bg-slate-700" onClick={() => setSelectedTable(null)}>
                                    <X className="w-3 h-3" />
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Premium Tabs */}
            <div className="bg-white rounded-xl shadow-sm border p-1.5 inline-flex flex-wrap gap-1">
                {TABS.map(t => {
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`pos-tab-${t.id}`}
                            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${active
                                ? 'bg-gradient-to-br from-slate-800 to-slate-900 text-white shadow-md'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                }`}
                        >
                            <t.icon className="w-4 h-4" />
                            <span>{t.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* Tab content */}
            {tab === 'order' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    {/* Left: Masalar + Menü */}
                    <div className="lg:col-span-2 space-y-3">
                        {/* Masalar */}
                        <Card>
                            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <LayoutGrid className="w-4 h-4 text-emerald-600" /> Masalar
                                </CardTitle>
                                <div className="text-xs text-slate-500">
                                    {selectedTable ? <>Seçili: <b className="text-emerald-700">{selectedTable.name}</b></> : 'Bir masa seçin'}
                                </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                                {tables.length === 0 ? (
                                    <div className="text-center py-4 text-slate-400 text-sm">Masa tanımlı değil. Menü Yönetimi&apos;nden ekleyin.</div>
                                ) : (
                                    <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                                        {tables.map(t => (
                                            <button
                                                key={t.id}
                                                onClick={() => setSelectedTable(t)}
                                                data-testid={`pos-table-${t.name}`}
                                                className={`aspect-square rounded-lg border-2 font-bold text-sm transition-all shadow-sm hover:shadow-md ${selectedTable?.id === t.id ? 'bg-emerald-600 text-white border-emerald-700 scale-105' : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'}`}
                                            >
                                                <div>{t.name}</div>
                                                {t.zone && <div className="text-[10px] font-normal opacity-70">{t.zone}</div>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Menü */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <UtensilsCrossed className="w-4 h-4 text-emerald-600" /> Menü
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                                {menu.categories.length === 0 ? (
                                    <div className="text-center py-4 text-slate-400 text-sm">Kategori/ürün tanımlı değil.</div>
                                ) : (
                                    <>
                                        <div className="flex gap-1 mb-2 flex-wrap">
                                            {menu.categories.map(c => (
                                                <Button
                                                    key={c.id}
                                                    size="sm"
                                                    variant={activeCat === c.id ? 'default' : 'outline'}
                                                    onClick={() => setActiveCat(c.id)}
                                                    data-testid={`pos-cat-${c.id}`}
                                                    className={activeCat === c.id ? (c.kind === 'yiyecek' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700') : ''}
                                                >
                                                    {c.kind === 'yiyecek' ? <UtensilsCrossed className="w-3 h-3 mr-1" /> : <Wine className="w-3 h-3 mr-1" />}
                                                    {c.name}
                                                </Button>
                                            ))}
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-1">
                                            {menu.items.filter(m => m.category_id === activeCat).map(m => (
                                                <button
                                                    key={m.id}
                                                    onClick={() => addToCart(m)}
                                                    data-testid={`pos-item-${m.id}`}
                                                    className="text-left bg-white border rounded-lg hover:border-emerald-400 hover:shadow-md transition-all overflow-hidden group"
                                                >
                                                    <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden relative">
                                                        {m.photo_url ? (
                                                            <img src={m.photo_url.startsWith('http') ? m.photo_url : `${process.env.REACT_APP_BACKEND_URL}${m.photo_url}`} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-slate-300 text-4xl">
                                                                {m.kind === 'yiyecek' ? '🍽' : '🥤'}
                                                            </div>
                                                        )}
                                                        <Badge className={`absolute top-1 right-1 text-[9px] ${m.kind === 'yiyecek' ? 'bg-amber-500 text-white' : 'bg-purple-500 text-white'}`}>
                                                            {KIND_LBL[m.kind]}
                                                        </Badge>
                                                    </div>
                                                    <div className="p-2">
                                                        <div className="font-semibold text-sm text-slate-800 truncate">{m.name}</div>
                                                        <div className="text-sm text-emerald-700 font-bold mt-0.5">{Number(m.price).toFixed(2)} TL</div>
                                                    </div>
                                                </button>
                                            ))}
                                            {menu.items.filter(m => m.category_id === activeCat).length === 0 && (
                                                <div className="col-span-full text-center py-4 text-slate-400 text-sm">Bu kategoride ürün yok</div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right: Sepet */}
                    <div>
                        <Card className="sticky top-4">
                            <CardHeader className="pb-2 bg-gradient-to-r from-emerald-50 to-white">
                                <CardTitle className="text-sm flex items-center justify-between">
                                    <span className="flex items-center gap-2"><ShoppingCart className="w-4 h-4 text-emerald-600" /> Sepet</span>
                                    <Badge className="bg-emerald-600">{cartCount} adet</Badge>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-2 space-y-2">
                                {!selectedTable && (
                                    <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">Masa seçilmedi</div>
                                )}
                                {cart.length === 0 ? (
                                    <div className="text-center py-8 text-slate-400 text-sm">
                                        <ShoppingCart className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                                        Sepet boş.<br />Sol taraftan ürün ekleyin.
                                    </div>
                                ) : (
                                    <div className="max-h-[280px] overflow-y-auto space-y-1.5">
                                        {cart.map(c => (
                                            <div key={c.menu_item_id} className="bg-slate-50 rounded p-2" data-testid={`pos-cart-${c.menu_item_id}`}>
                                                <div className="flex justify-between items-start gap-2">
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium text-slate-800 truncate">{c.name}</div>
                                                        <div className="text-[11px] text-slate-500">{Number(c.price).toFixed(2)} TL × {c.qty}</div>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => decCart(c.menu_item_id)}>-</Button>
                                                        <span className="w-6 text-center font-bold text-sm">{c.qty}</span>
                                                        <Button size="sm" className="h-6 w-6 p-0 bg-emerald-600 hover:bg-emerald-700" onClick={() => addToCart({ id: c.menu_item_id, name: c.name, price: c.price, kind: c.kind })}>+</Button>
                                                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => removeCart(c.menu_item_id)}><X className="w-3 h-3" /></Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <Textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Sipariş notu (opsiyonel — alerjen, özel istek…)"
                                    data-testid="pos-note"
                                    className="text-xs"
                                    rows={2}
                                />
                                <div className="border-t pt-2 flex items-center justify-between">
                                    <span className="text-xs text-slate-500">Toplam</span>
                                    <span className="text-lg font-bold text-emerald-700">{cartTotal.toFixed(2)} TL</span>
                                </div>
                                <Button
                                    onClick={submitOrder}
                                    disabled={submitting || !cart.length || !selectedTable}
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid="pos-submit"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                    POS&apos;a Gönder
                                </Button>
                                {cart.length > 0 && (
                                    <Button variant="outline" onClick={clearCart} className="w-full h-8 text-xs text-slate-500">
                                        <Trash2 className="w-3 h-3 mr-1" /> Sepeti Temizle
                                    </Button>
                                )}
                                {lastOrder && (
                                    <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center" data-testid="pos-last-order">
                                        <div className="text-[11px] text-emerald-700">Son Sipariş</div>
                                        <div className="text-2xl font-black text-emerald-700 font-mono">#{lastOrder.order_no}</div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            {tab === 'kds' && (
                <div className="space-y-4">
                    <div>
                        <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><UtensilsCrossed className="w-3 h-3 text-amber-600" /> Mutfak</div>
                        <InlineKDS station="kitchen" />
                    </div>
                    <div>
                        <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Wine className="w-3 h-3 text-purple-600" /> Bar</div>
                        <InlineKDS station="bar" />
                    </div>
                </div>
            )}

            {tab === 'display' && <InlineDisplay />}
            {tab === 'menu-admin' && isAdmin && <SnackBarAdmin />}
            {tab === 'reports' && isAdmin && <SnackBarReports />}
        </div>
    );
}

// ===== Inline KDS (POS içindeki küçültülmüş görünüm) =====
function InlineKDS({ station }) {
    const [orders, setOrders] = useState([]);
    const load = useCallback(async () => {
        try { const r = await api.get('/snack-bar/orders', { params: { station, active_only: true } }); setOrders(r.data.items || []); } catch { /* */ }
    }, [station]);
    useEffect(() => { load(); const i = setInterval(load, 3000); return () => clearInterval(i); }, [load]);
    const markReady = async (oid, lineId) => {
        try { await api.post(`/snack-bar/orders/${oid}/items/${lineId}/ready`); load(); }
        catch { toast.error('Hata'); }
    };
    if (orders.length === 0) return <div className="text-center py-6 bg-white rounded border text-slate-400 text-sm">Bekleyen sipariş yok</div>;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2" data-testid={`pos-kds-${station}`}>
            {orders.map(o => (
                <Card key={o.id} className="border-l-4 border-amber-500">
                    <CardHeader className="pb-1 py-2">
                        <div className="flex justify-between items-center">
                            <CardTitle className="text-sm font-mono text-amber-700">#{o.order_no}</CardTitle>
                            <Badge className="bg-slate-800 text-white text-[10px]">{o.table_name}</Badge>
                        </div>
                        <div className="text-[10px] text-slate-500 flex justify-between">
                            <span>{new Date(o.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                            {o.waiter_name && <span className="text-emerald-700">{o.waiter_name}</span>}
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-1 pt-0 pb-2">
                        {o.items.filter(it => it.station === station).map(it => (
                            <div key={it.line_id} className={`flex justify-between items-center px-2 py-1 rounded text-xs ${it.status === 'ready' ? 'bg-emerald-50 text-slate-500 line-through' : 'bg-slate-50'}`}>
                                <div className="truncate"><b>{it.qty}×</b> {it.name}</div>
                                {it.status !== 'ready' && <Button size="sm" onClick={() => markReady(o.id, it.line_id)} className="bg-emerald-600 hover:bg-emerald-700 h-6 text-[10px] px-1.5"><Check className="w-3 h-3" /></Button>}
                            </div>
                        ))}
                        {o.guest_note && <div className="text-[10px] bg-yellow-50 text-yellow-800 p-1 rounded">Not: {o.guest_note}</div>}
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}

// ===== Inline Display (POS içindeki küçültülmüş sıramatik) =====
function InlineDisplay() {
    const [items, setItems] = useState([]);
    const load = useCallback(async () => {
        try { const r = await api.get('/snack-bar/display'); setItems(r.data.items || []); } catch { /* */ }
    }, []);
    useEffect(() => { load(); const i = setInterval(load, 3000); return () => clearInterval(i); }, [load]);
    return (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6" data-testid="pos-display">
            <div className="text-center mb-4">
                <h2 className="text-3xl font-black text-emerald-400">SİPARİŞİNİZ HAZIR</h2>
                <p className="text-slate-400 text-sm mt-1">Aşağıdaki sipariş numaraları hazır — servis bekleniyor</p>
            </div>
            {items.length === 0 ? (
                <div className="text-center text-slate-500 text-lg py-8">Şu an hazır sipariş yok</div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {items.map(o => (
                        <div key={o.order_no} className="bg-emerald-500 text-white rounded-2xl p-4 text-center shadow-lg animate-pulse">
                            <div className="text-[10px] opacity-80">Sipariş No</div>
                            <div className="text-3xl font-black my-1 font-mono">{o.order_no}</div>
                            <div className="text-sm">{o.table_name}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


// ===== QR Admin — Tek Genel QR =====
function QrAdmin() {
    const [QRCodeMod, setQRCodeMod] = React.useState(null);
    React.useEffect(() => { import('qrcode.react').then(m => setQRCodeMod(() => m.QRCodeSVG || m.default)); }, []);
    const menuUrl = `${window.location.origin}/menu`;
    const tvUrl = `${window.location.origin}/snack-bar/tv`;
    const printQr = () => {
        const w = window.open('', '_blank');
        w.document.write(`<html><head><title>Grand Kaptan - QR Menü</title><style>body{font-family:sans-serif;text-align:center;padding:40px;} h1{color:#065f46;margin:8px 0;} .qr{margin:20px auto;} .u{font-size:11px;color:#64748b;word-break:break-all;}</style></head><body><h1>GRAND KAPTAN</h1><h2 style="margin:0;color:#059669;">SNACK BAR — QR MENÜ</h2><p style="color:#475569;">QR kodu okutun, masanızı seçin, siparişinizi verin.</p><div class="qr">${document.querySelector('[data-testid="qr-svg-menu"]')?.outerHTML || ''}</div><p class="u">${menuUrl}</p><p style="margin-top:40px;color:#94a3b8;font-size:12px;">Afiyet olsun · Grand Kaptan Hotel</p></body></html>`);
        w.document.close(); setTimeout(() => w.print(), 500);
    };
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="qr-admin">
            <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2">📱 Misafir QR Menü (Tek Genel)</CardTitle></CardHeader>
                <CardContent className="text-center space-y-3">
                    <div className="bg-white p-4 rounded-xl border inline-block">
                        {QRCodeMod && <QRCodeMod value={menuUrl} size={220} level="M" data-testid="qr-svg-menu" />}
                    </div>
                    <div className="text-xs text-slate-500 break-all">{menuUrl}</div>
                    <div className="flex gap-2 justify-center">
                        <Button onClick={printQr} className="bg-emerald-600 hover:bg-emerald-700" data-testid="qr-print"><Printer className="w-4 h-4 mr-1" /> Yazdır (A4)</Button>
                        <Button variant="outline" onClick={() => { navigator.clipboard.writeText(menuUrl); toast.success('Kopyalandı'); }}>URL Kopyala</Button>
                    </div>
                    <div className="text-[11px] text-slate-500 bg-emerald-50 p-2 rounded">Misafir QR okur → masa seçer → menü → sipariş tamamlar.</div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2">📺 TV Tanıtım Ekranı</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                    <div className="bg-slate-900 text-white p-4 rounded-xl">
                        <div className="text-xs uppercase tracking-widest text-emerald-300">TV URL</div>
                        <div className="text-sm font-mono mt-1 break-all">{tvUrl}</div>
                    </div>
                    <div className="text-xs text-slate-600 space-y-1">
                        <div>• TV&apos;nizde tam ekran açın</div>
                        <div>• Sipariş hazır olunca numara ortada 8sn yanıp söner + bell çalar</div>
                        <div>• TV Tanıtım sekmesinden slideshow için görsel/video yükleyin</div>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={() => window.open(tvUrl, '_blank')} className="bg-slate-800 hover:bg-slate-900"><Monitor className="w-4 h-4 mr-1" /> TV Ekranını Aç</Button>
                        <Button variant="outline" onClick={() => { navigator.clipboard.writeText(tvUrl); toast.success('Kopyalandı'); }}>URL Kopyala</Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ===== TV Media Admin — Slideshow yönetimi =====
function TvMediaAdmin() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ url: '', kind: 'image', title: '', duration_sec: 6, sort_order: 100 });
    const [uploading, setUploading] = useState(false);
    const load = async () => { try { const r = await api.get('/snack-bar/tv-media'); setItems(r.data.items || []); } catch { /* */ } };
    useEffect(() => { load(); }, []);
    const uploadFile = async (file) => {
        setUploading(true);
        try {
            const fd = new FormData(); fd.append('file', file);
            const r = await api.post('/snack-bar/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const kind = file.type.startsWith('video/') ? 'video' : 'image';
            setForm(f => ({ ...f, url: r.data.url, kind }));
            toast.success('Yüklendi');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Yükleme başarısız'); }
        finally { setUploading(false); }
    };
    const add = async () => {
        if (!form.url) return toast.error('Önce dosya yükleyin veya URL girin');
        try { await api.post('/snack-bar/tv-media', { ...form, duration_sec: Number(form.duration_sec) || 6, sort_order: Number(form.sort_order) || 100 }); toast.success('Eklendi'); setForm({ url: '', kind: 'image', title: '', duration_sec: 6, sort_order: 100 }); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Hata'); }
    };
    const del = async (id) => { if (!window.confirm('Silinsin mi?')) return; try { await api.delete(`/snack-bar/tv-media/${id}`); toast.success('Silindi'); load(); } catch { toast.error('Hata'); } };
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="tv-admin">
            <div className="lg:col-span-1">
                <Card>
                    <CardHeader><CardTitle className="text-base">➕ Medya Ekle</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        <Label className="text-xs">Dosya Yükle (görsel/video)</Label>
                        <Input type="file" accept="image/*,video/*" onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])} data-testid="tv-file-input" />
                        {uploading && <div className="text-xs text-emerald-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Yükleniyor…</div>}
                        {form.url && <div className="text-xs text-emerald-700 truncate">✓ {form.url}</div>}
                        <Label className="text-xs mt-2">Başlık (opsiyonel)</Label>
                        <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Örn: Yeni Sezon Kokteyller" />
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label className="text-xs">Süre (sn)</Label><Input type="number" value={form.duration_sec} onChange={e => setForm(f => ({ ...f, duration_sec: e.target.value }))} /></div>
                            <div><Label className="text-xs">Sıra</Label><Input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} /></div>
                        </div>
                        <Button onClick={add} className="w-full bg-emerald-600 hover:bg-emerald-700 mt-2" data-testid="tv-add-btn"><Plus className="w-4 h-4 mr-1" /> Ekle</Button>
                    </CardContent>
                </Card>
            </div>
            <div className="lg:col-span-2">
                <Card>
                    <CardHeader><CardTitle className="text-base">Slideshow Sırası ({items.length})</CardTitle></CardHeader>
                    <CardContent>
                        {items.length === 0 ? <div className="text-center py-8 text-slate-400 text-sm">Henüz medya yok</div> :
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {items.map(m => (
                                    <div key={m.id} className="relative border rounded-lg overflow-hidden bg-slate-50">
                                        {m.kind === 'video' ?
                                            <video src={m.url.startsWith('http') ? m.url : `${process.env.REACT_APP_BACKEND_URL}${m.url}`} className="w-full h-32 object-cover" muted /> :
                                            <img src={m.url.startsWith('http') ? m.url : `${process.env.REACT_APP_BACKEND_URL}${m.url}`} alt="" className="w-full h-32 object-cover" />}
                                        <div className="p-2">
                                            <div className="text-xs font-semibold truncate">{m.title || '(başlıksız)'}</div>
                                            <div className="text-[10px] text-slate-500">{m.kind} · {m.duration_sec}sn · sıra:{m.sort_order}</div>
                                        </div>
                                        <button onClick={() => del(m.id)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                ))}
                            </div>}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

